import { useState, useEffect, useCallback } from "react";
import { T, API, SECTIONS, GALLERY_CATS, Icon } from "../AdminPanel";
import { useApi } from "../AdminPanel";

export default function ImagesPage({ token, toast, onExpired }) {
  const { get, del, patch } = useApi(token, onExpired);
  const [images, setImages]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [section, setSection]   = useState("all");
  const [editing, setEditing]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = section !== "all" ? `?section=${section}` : "/";
      const data = await get(`/images${section !== "all" ? q : "/"}`);
      setImages(data);
    } catch { toast("Ошибка загрузки", "error"); }
    finally   { setLoading(false); }
  }, [get, section, toast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (img) => {
    setEditing(img);
    setEditForm({
      title:       img.title,
      description: img.description || "",
      section:     img.section,
      category:    img.category || "",
      sort_order:  img.sort_order,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await patch(`/images/${editing.id}/meta`, editForm);
      toast("Сохранено", "success");
      setEditing(null);
      load();
    } catch { toast("Ошибка сохранения", "error"); }
    finally   { setSaving(false); }
  };

  const confirmDelete = async (id) => {
    try {
      await del(`/images/${id}`);
      toast("Изображение удалено", "success");
      setDeleting(null);
      setImages(prev => prev.filter(img => img.id !== id));
    } catch { toast("Ошибка удаления", "error"); }
  };

  const counts = { all: images.length };
  for (const s of SECTIONS) counts[s.value] = images.filter(i => i.section === s.value).length;

  const visible = section === "all" ? images : images.filter(i => i.section === section);

  return (
    <div>
      <div className="adm-eyebrow">Медиабиблиотека</div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Oswald', sans-serif", fontSize: 32, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Управление изображениями
        </h1>
        <button className="adm-btn" onClick={load}>{Icon.Refresh} Обновить</button>
      </div>

      <div className="adm-filters">
        {[["all", "Все"], ...SECTIONS.map(s => [s.value, s.label])].map(([v, l]) => (
          <button key={v} className={`adm-filter-btn${section === v ? " active" : ""}`} onClick={() => setSection(v)}>
            <span>{l}</span>
            <span className="adm-filter-count">{counts[v] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="adm-empty"><span className="adm-spinner" /></div>
      ) : visible.length === 0 ? (
        <div className="adm-empty">Нет изображений в этом разделе</div>
      ) : (
        <div className="adm-img-grid">
          {visible.map(img => (
            <div key={img.id} className="adm-img-item">
              <img src={`${API}/images/${img.id}/file`} alt={img.title} loading="lazy"
                onError={e => { e.target.style.opacity = 0.2; }} />
              <div className="adm-img-item__meta">
                <span className="adm-section-chip" data-section={img.section}>{img.section}</span>
              </div>
              <div className="adm-img-item__order">#{img.sort_order}</div>
              <div className="adm-img-item__title">{img.title || img.filename}</div>
              <div className="adm-img-item__overlay">
                <div className="adm-img-item__actions">
                  <button className="adm-img-btn" title="Редактировать" onClick={() => startEdit(img)}>{Icon.Edit}</button>
                  <button className="adm-img-btn adm-img-btn--danger" title="Удалить" onClick={() => setDeleting(img)}>{Icon.Trash}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="adm-modal-wrap" onClick={e => e.target === e.currentTarget && setEditing(null)}>
          <div className="adm-modal">
            <div className="adm-modal__header">
              <span className="adm-modal__title">Редактирование</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setEditing(null)}>{Icon.Close}</button>
            </div>
            <div className="adm-modal__body">
              <img src={`${API}/images/${editing.id}/file`} className="adm-preview-thumb" alt="" />
              <div className="adm-form">
                <div className="adm-form-row">
                  <div className="adm-field">
                    <label className="adm-label">Блок сайта</label>
                    <select className="adm-select" value={editForm.section} onChange={e => setEditForm(f => ({ ...f, section: e.target.value }))}>
                      {SECTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Порядок</label>
                    <input className="adm-input" type="number" value={editForm.sort_order}
                      onChange={e => setEditForm(f => ({ ...f, sort_order: Number(e.target.value) }))} />
                  </div>
                </div>
                <div className="adm-field">
                  <label className="adm-label">Заголовок</label>
                  <input className="adm-input" value={editForm.title}
                    onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Категория</label>
                  {editForm.section === "gallery" ? (
                    <select className="adm-select" value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}>
                      <option value="">— без категории —</option>
                      {GALLERY_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input className="adm-input" value={editForm.category}
                      onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} placeholder="опционально" />
                  )}
                </div>
                <div className="adm-field">
                  <label className="adm-label">Описание</label>
                  <textarea className="adm-textarea" value={editForm.description}
                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn" onClick={() => setEditing(null)}>Отмена</button>
              <button className="adm-btn adm-btn--primary" onClick={saveEdit} disabled={saving}>
                {saving ? <span className="adm-spinner" /> : Icon.Check}
                {saving ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleting && (
        <div className="adm-modal-wrap" onClick={e => e.target === e.currentTarget && setDeleting(null)}>
          <div className="adm-modal" style={{ maxWidth: 400 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Подтверждение</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setDeleting(null)}>{Icon.Close}</button>
            </div>
            <div className="adm-modal__body">
              <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 16 }}>
                Удалить изображение <strong style={{ color: T.text }}>{deleting.title || deleting.filename}</strong>? Это действие необратимо.
              </p>
              <img src={`${API}/images/${deleting.id}/file`} className="adm-preview-thumb" alt="" style={{ maxHeight: 120 }} />
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn" onClick={() => setDeleting(null)}>Отмена</button>
              <button className="adm-btn adm-btn--danger" onClick={() => confirmDelete(deleting.id)}>
                {Icon.Trash} Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}